<template>
  <div id="weather-time-controller">
    <vs-row vs-type="flex contoroller-header" vs-align="center">
      <vs-col vs-w="2">
        <img :src=playIconType class="cursor-pointer" @click="onPlay">
      </vs-col>
      <vs-col vs-w="10">
        <vue-slider
          ref="slider"
          v-model="value"
          :marks="marks"
          :tooltip-formatter="formatter"
          v-bind="options"
          @change="onChange">
          <template v-slot:label="{ label, active }">
            <div :class="['vue-slider-mark-label', 'aisea-text-18-400-white']">{{ label }}</div>
            <div :class="['custom-step', { active }]"></div>
          </template>
        </vue-slider>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import VueSlider from "vue-slider-component";
import "vue-slider-component/theme/antd.css";
import {createNamespacedHelpers} from "vuex";
import moment from "moment-timezone";

// Mixins
import {ColorMixin} from "@/mixins";


// Store
const {
  mapMutations: mapMutationsOfLiveMap,
  mapState: mapStateOfLiveMap,
  mapGetters: mapGettersOfLiveMap
} = createNamespacedHelpers("liveMap");

export default {
  name: "WeatherTimeController",
  mixins: [ColorMixin],
  components: {
    VueSlider
  },
  data() {
    return {
      playIcon: require("@/assets/images/weather/weather_play.svg"),
      pauseIcon: require("@/assets/images/weather/weather_pause.svg"),
      isPlay: false,
      ended: false,
      intervalId: null,
      value: 0,
      marks: val => val % 24 === 0 ? ({
        label: `${this.timeLabel(val)}`,
      }) : false,
      formatter: val => `${this.tooltipLabel(val)}`,
      options: {
        width: 'auto',
        height: 8,
        min: 0,
        max: 71,
        interval: 1,
        duration: 0.5,
        lazy: true,
        tooltip: "hover",
        marks: false,
        tooltipStyle: {"background-color": "#48A0F4", "border-color": "#48A0F4"}
      }
    };
  },
  computed: {
    ...mapStateOfLiveMap({
      metoceanInitialDatetime: state => state.metoceanInitialDatetime,
      showWeatherMenu: state => state.showWeatherMenu,
      showContour: state => state.showContour,
    }),
    ...mapGettersOfLiveMap(["getMetoceanInitialDatetime", "getCurrentMetoceanSourceLayerId", "getCurrentContourSourceLayerId"]),
    playIconType() {
      return this.isPlay ? this.pauseIcon : this.playIcon;
    },
  },
  watch: {},
  methods: {
    ...mapMutationsOfLiveMap([
      "SET_METOCEAN_CURRENT_DATETIME",
      "SET_CURRENT_WEATHER_SOURCE_LAYER",
      "SET_UPDATE_METOCEAN_SOURCE_LAYER",
      "SET_UPDATE_CONTOUR_SOURCE_LAYER",
    ]),
    timeLabel(val) {
      let initialDatetime = this.getMetoceanInitialDatetime
      return moment.unix(initialDatetime).add(val, 'h').format('D日(dd)')
    },
    tooltipLabel(val) {
      let initialDatetime = this.getMetoceanInitialDatetime
      return moment.unix(initialDatetime).add(val, 'h').format('HH:mm')
    },
    onChange() {
      this.setCurrentTime()
    },
    setCurrentTime() {
      let initialDatetime = this.getMetoceanInitialDatetime
      let currentSourceLayerId = this.getCurrentMetoceanSourceLayerId
      const newMomentObjJST = moment.unix(initialDatetime).add(this.value, 'h');
      const newMomentObjUTC = newMomentObjJST.clone().utc();
      this.SET_METOCEAN_CURRENT_DATETIME(newMomentObjJST.format('M月D日 (dd) HH:mm'));

      // 海象
      if (this.showWeatherMenu) {
        let newSourceLayerId = `metocean_${newMomentObjUTC.format('YYYYMMDDHHmm')}`;
        this.SET_CURRENT_WEATHER_SOURCE_LAYER(newSourceLayerId);
        this.SET_UPDATE_METOCEAN_SOURCE_LAYER({
          isUpdate: true,
          oldSourceLayerId: currentSourceLayerId,
          newSourceLayerId: newSourceLayerId,
          displayedUnixTime: newMomentObjUTC.unix(),
        })
      }

      // 等圧線
      if (this.showContour) {
        const currentContourSourceLayerId = this.getCurrentContourSourceLayerId;
        // 等圧線は6時間毎の時間のデータを表示する: e.g. UTC 2000-01-01 00:00, 2000-01-01 06:00, 2000-01-01 12:00, 2000-01-01 18:00
        const start = moment().utc().startOf("day");
        const diffHours = newMomentObjUTC.diff(start, "hours");
        const newContourSourceTime = start.add(Math.floor(diffHours / 6) * 6, 'h').format('YYYYMMDDHHmm');
        const newContourSourceLayerId = `contour_${newContourSourceTime}`;
        if (currentContourSourceLayerId != newContourSourceLayerId) {
          this.SET_UPDATE_CONTOUR_SOURCE_LAYER({
            isUpdate: true,
            oldSourceLayerId: currentContourSourceLayerId,
            newSourceLayerId: newContourSourceLayerId,
            displayedUnixTime: newMomentObjUTC.unix(),
          });
        }
      }
    },
    onPlay() {
      this.isPlay = !this.isPlay
      if (this.isPlay) {
        if (this.options.max === this.value) {
          this.value = 0
        } else {
          this.value = this.value + 1
        }
        this.setCurrentTime()
        this.intervalId = this.$setInterval(() => {
          if (this.options.max - 1 >= this.value) {
            this.value = this.value + 1
            this.setCurrentTime()
          } else {
            this.$clearInterval(this.intervalId)
            this.intervalId = null
            this.isPlay = false
          }
        }, 3000);
      } else {
        this.$clearInterval(this.intervalId)
        this.intervalId = null
      }
    }
  },
  mounted() {
    let initialDatetime = this.getMetoceanInitialDatetime
    let dateTo = moment.unix(initialDatetime)
    let dateFrom = moment().startOf('hours')
    this.value = dateFrom.diff(dateTo, 'hours')
    this.setCurrentTime()
  },
  created() {
    moment.locale('ja')
  },
  beforeDestroy() {
    if (this.intervalId) {
      this.$clearInterval(this.intervalId);
    }
  }
}
</script>

<style lang="scss">
@import "src/assets/scss/aisea/variables";

#weather-time-controller {
  background: #333333;
  height: 100px;
  padding-top: 30px;
  padding-left: 20px;
  padding-right: 20px;

  .custom-label {
    color: #FFFFFF;
    padding-left: 40px;
  }

  .custom-label.active {
    color: $aisea-blue !important;
  }

  .vue-slider-dot-tooltip-text {
    font-family: $aisea-font-family !important;
  }

  .custom-step {
    width: 0;
    height: 0;
  }

  .container {
    display: table;
    height: 200px;
  }

  .vue-slider-process {
    background: $aisea-blue !important;
  }

  .contoroller-header {
    min-height: 34px;
  }

}

</style>
